/**
 * MinistrareIA - WhatsApp Business Templates Page
 * Manage Meta message templates (create, list, delete, send, view history)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tags, Plus, Trash2, X, Loader2, Check, AlertCircle,
  PanelLeftOpen, PanelLeftClose, Search, RefreshCw,
  Send, History, Phone, ChevronLeft, ChevronRight,
  CheckCheck, Clock, XCircle, ArrowUpRight,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import templateService from '../../services/global/templateService';


const TemplatesPage = ({ onToggleNav, isNavOpen }) => {
  const { colors } = useTheme();

  // State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Create form
  const [formName, setFormName] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formCategory, setFormCategory] = useState('UTILITY');
  const [formLanguage, setFormLanguage] = useState('pt_BR');
  // Variables (optional). Each: { key, source, label }. Order = placeholder position (1-indexed).
  const [formVariables, setFormVariables] = useState([]);

  // Send modal
  const [sendModal, setSendModal] = useState(null); // template object or null
  const [sendPhone, setSendPhone] = useState('');
  const [sending, setSending] = useState(false);

  // History modal
  const [historyModal, setHistoryModal] = useState(null); // template name or null
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPageSize] = useState(20);
  const [historyPhoneFilter, setHistoryPhoneFilter] = useState('');
  const debounceRef = useRef(null);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateService.list();
      setTemplates(data.templates || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const semAcesso = /does not exist|missing permissions|Unsupported get request|Invalid OAuth|access token/i.test(detail);
      setError(semAcesso
        ? 'Sem acesso à conta do WhatsApp na Meta (a conta do número perdeu a autorização). Os templates continuam existindo na Meta. Reconecte o número em Conexão ou reatribua a WABA ao usuário do sistema no Meta Business.'
        : (detail ? `Erro ao carregar templates: ${detail}` : 'Erro ao carregar templates'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Create template
  const handleCreate = async () => {
    if (!formName.trim() || !formBody.trim()) return;

    // Validate every declared variable has a matching {{N}} placeholder in body
    for (let i = 0; i < formVariables.length; i++) {
      const v = formVariables[i];
      const ph = `{{${i + 1}}}`;
      if (!v.key?.trim() || !v.source?.trim()) {
        setError(`Variável #${i + 1}: preencha chave e fonte`);
        return;
      }
      if (!formBody.includes(ph)) {
        setError(`Variável #${i + 1}: o texto precisa conter o marcador ${ph}`);
        return;
      }
    }

    try {
      setCreating(true);
      setError(null);
      const payload = {
        name: formName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        body_text: formBody.trim(),
        category: formCategory,
        language: formLanguage,
      };
      if (formVariables.length > 0) {
        payload.variables = formVariables.map((v, idx) => {
          // "custom:livre" is a UI placeholder — resolve it to custom:<key> at submit
          const source = v.source === 'custom:livre' ? `custom:${v.key.trim()}` : v.source.trim();
          return {
            key: v.key.trim(),
            source,
            label: (v.label || v.key).trim(),
            position: idx + 1,
          };
        });
      }
      await templateService.create(payload);
      setShowCreateModal(false);
      setFormName('');
      setFormBody('');
      setFormCategory('UTILITY');
      setFormVariables([]);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao criar template');
    } finally {
      setCreating(false);
    }
  };

  // Variables helpers
  const addVariable = () => {
    const nextIdx = formVariables.length + 1;
    setFormVariables(arr => [...arr, { key: '', source: 'system:user_name', label: '' }]);
    // Auto-append placeholder hint at cursor end if missing
    const ph = `{{${nextIdx}}}`;
    if (!formBody.includes(ph)) {
      setFormBody(b => `${b}${b.endsWith(' ') || !b ? '' : ' '}${ph}`);
    }
  };
  const removeVariable = (idx) => {
    setFormVariables(arr => arr.filter((_, i) => i !== idx));
  };
  const updateVariable = (idx, patch) => {
    setFormVariables(arr => arr.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  // Delete template
  const handleDelete = async (name) => {
    if (!window.confirm(`Deletar template "${name}"?`)) return;

    try {
      setDeleting(name);
      await templateService.delete(name);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao deletar template');
    } finally {
      setDeleting(null);
    }
  };

  // Send template
  const handleSend = async () => {
    if (!sendPhone.trim() || !sendModal) return;

    try {
      setSending(true);
      setError(null);
      await templateService.send(sendModal.name, sendPhone.trim());
      setSuccessMsg(`Template enviado para ${sendPhone}`);
      setSendModal(null);
      setSendPhone('');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao enviar template');
    } finally {
      setSending(false);
    }
  };

  // Load history logs
  const loadHistoryLogs = useCallback(async (templateName, page = 1, phone = '') => {
    try {
      setHistoryLoading(true);
      const data = await templateService.getLogs(templateName, {
        page,
        pageSize: historyPageSize,
        phone,
      });
      setHistoryLogs(data.logs || []);
      setHistoryTotal(data.total || 0);
      setHistoryPage(data.page || 1);
    } catch (err) {
      console.error('Error loading logs:', err);
      setHistoryLogs([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPageSize]);

  // Open history modal
  const openHistory = (templateName) => {
    setHistoryModal(templateName);
    setHistoryPhoneFilter('');
    setHistoryPage(1);
    loadHistoryLogs(templateName, 1, '');
  };

  // History phone filter with debounce
  const handleHistoryPhoneChange = (value) => {
    setHistoryPhoneFilter(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setHistoryPage(1);
      loadHistoryLogs(historyModal, 1, value);
    }, 500);
  };

  // History pagination
  const handleHistoryPageChange = (newPage) => {
    setHistoryPage(newPage);
    loadHistoryLogs(historyModal, newPage, historyPhoneFilter);
  };

  const totalPages = Math.ceil(historyTotal / historyPageSize);

  // Filter templates
  const filtered = templates.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Status badge
  const StatusBadge = ({ status }) => {
    const map = {
      APPROVED: { bg: '#10b98120', color: '#10b981', label: 'Aprovado' },
      PENDING: { bg: '#f59e0b20', color: '#f59e0b', label: 'Pendente' },
      REJECTED: { bg: '#ef444420', color: '#ef4444', label: 'Rejeitado' },
    };
    const s = map[status] || { bg: '#6b728020', color: '#6b7280', label: status || 'Desconhecido' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
        fontWeight: 600, backgroundColor: s.bg, color: s.color,
      }}>
        {s.label}
      </span>
    );
  };

  // Quality tag
  const QualityTag = ({ score }) => {
    const scoreStr = typeof score === 'object' ? score?.score : score;
    const map = {
      HIGH: { bg: '#10b98118', color: '#10b981', label: 'Alta' },
      MEDIUM: { bg: '#f59e0b18', color: '#f59e0b', label: 'Media' },
      LOW: { bg: '#ef444418', color: '#ef4444', label: 'Baixa' },
    };
    const key = typeof scoreStr === 'string' ? scoreStr.toUpperCase() : null;
    const q = map[key] || { bg: `${colors.textTertiary}15`, color: colors.textTertiary, label: scoreStr || 'N/A' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '2px 8px', borderRadius: '6px', fontSize: '11px',
        fontWeight: 500, backgroundColor: q.bg, color: q.color,
      }}>
        {q.label}
      </span>
    );
  };

  // Log status badge
  const LogStatusBadge = ({ status }) => {
    const map = {
      sent: { bg: '#3b82f618', color: '#3b82f6', label: 'Enviado', icon: <ArrowUpRight size={12} /> },
      delivered: { bg: '#10b98118', color: '#10b981', label: 'Entregue', icon: <Check size={12} /> },
      read: { bg: '#6366f118', color: '#6366f1', label: 'Lido', icon: <CheckCheck size={12} /> },
      failed: { bg: '#ef444418', color: '#ef4444', label: 'Falhou', icon: <XCircle size={12} /> },
    };
    const s = map[status] || { bg: '#6b728018', color: '#6b7280', label: status || '?', icon: <Clock size={12} /> };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
        fontWeight: 600, backgroundColor: s.bg, color: s.color,
      }}>
        {s.icon} {s.label}
      </span>
    );
  };

  // Get body text from components
  const getBodyText = (template) => {
    const body = template.components?.find(c => c.type === 'BODY');
    return body?.text || '';
  };

  // Format timestamp
  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const styles = {
    container: {
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgSecondary, height: '100vh', overflow: 'hidden',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 24px', backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`, minHeight: '60px',
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
    headerIcon: {
      width: '36px', height: '36px', borderRadius: '10px',
      backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
      alignItems: 'center', justifyContent: 'center', color: colors.accentPrimary,
    },
    content: { flex: 1, overflow: 'auto', padding: '24px' },
    searchBar: {
      display: 'flex', alignItems: 'center', gap: '12px',
      marginBottom: '20px',
    },
    searchInput: {
      flex: 1, padding: '8px 12px 8px 36px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
    },
    btn: {
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 500,
    },
    btnPrimary: {
      backgroundColor: colors.accentPrimary, color: '#fff',
    },
    btnGhost: {
      backgroundColor: 'transparent', color: colors.textSecondary,
      border: `1px solid ${colors.borderColor}`,
    },
    // Grid layout
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '16px',
    },
    card: {
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`, padding: '16px',
      display: 'flex', flexDirection: 'column',
      transition: 'border-color 0.2s',
    },
    cardHeader: {
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      marginBottom: '10px', gap: '8px',
    },
    cardName: {
      fontSize: '14px', fontWeight: 600, color: colors.textPrimary,
      fontFamily: 'monospace', wordBreak: 'break-all',
    },
    cardBody: {
      fontSize: '13px', color: colors.textSecondary, lineHeight: '1.5',
      padding: '10px 12px', borderRadius: '8px',
      backgroundColor: colors.bgSecondary, whiteSpace: 'pre-wrap',
      maxHeight: '72px', overflow: 'hidden', flex: 1,
    },
    cardTags: {
      display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
      marginTop: '10px',
    },
    tag: {
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: '6px', fontSize: '11px',
      fontWeight: 500, backgroundColor: `${colors.textTertiary}12`,
      color: colors.textTertiary,
    },
    cardActions: {
      display: 'flex', alignItems: 'center', gap: '6px',
      marginTop: '12px', paddingTop: '12px',
      borderTop: `1px solid ${colors.borderColor}`,
    },
    actionBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
      padding: '6px 12px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '12px', fontWeight: 500,
      backgroundColor: 'transparent', color: colors.textSecondary,
      transition: 'background-color 0.15s',
    },
    // Modal
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: '16px',
      width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
      border: `1px solid ${colors.borderColor}`,
    },
    modalLarge: {
      backgroundColor: colors.bgPrimary, borderRadius: '16px',
      width: '640px', maxWidth: '90vw', maxHeight: '85vh',
      border: `1px solid ${colors.borderColor}`,
      display: 'flex', flexDirection: 'column',
    },
    modalHeader: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
    },
    modalBody: { padding: '20px' },
    modalFooter: {
      display: 'flex', justifyContent: 'flex-end', gap: '8px',
      padding: '16px 20px', borderTop: `1px solid ${colors.borderColor}`,
    },
    formGroup: { marginBottom: '16px' },
    label: {
      display: 'block', fontSize: '13px', fontWeight: 500,
      color: colors.textPrimary, marginBottom: '6px',
    },
    input: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      minHeight: '120px', resize: 'vertical', fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', outline: 'none',
      boxSizing: 'border-box',
    },
    navToggleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '40px', height: '40px', border: 'none', background: 'transparent',
      color: colors.textSecondary, borderRadius: '8px', cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {onToggleNav && (
            <button onClick={onToggleNav} style={styles.navToggleBtn}>
              {isNavOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
          )}
          <div style={styles.headerIcon}><Tags size={20} /></div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
              Templates
            </h2>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
              {templates.length} template{templates.length !== 1 ? 's' : ''} da Meta
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{ ...styles.btn, ...styles.btnPrimary }}
        >
          <Plus size={16} /> Novo Template
        </button>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Success message */}
        {successMsg && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
            backgroundColor: '#10b98120', color: '#10b981', fontSize: '13px',
          }}>
            <Check size={16} /> {successMsg}
            <button
              onClick={() => setSuccessMsg(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
            backgroundColor: '#ef444420', color: '#ef4444', fontSize: '13px',
          }}>
            <AlertCircle size={16} /> {error}
            <button
              onClick={() => setError(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Search + Refresh */}
        <div style={styles.searchBar}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.textTertiary }} />
            <input
              placeholder="Buscar templates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <button onClick={loadTemplates} style={{ ...styles.btn, ...styles.btnGhost }}>
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>

        {/* Loading */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <Loader2 size={24} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '60px', gap: '12px',
          }}>
            <Tags size={40} color={colors.textTertiary} />
            <p style={{ fontSize: '14px', color: colors.textSecondary }}>
              {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template criado ainda'}
            </p>
          </div>
        ) : (
          /* Grid of cards */
          <div style={styles.grid}>
            {filtered.map(template => (
              <div
                key={`${template.name}-${template.language}`}
                style={styles.card}
                onMouseEnter={e => e.currentTarget.style.borderColor = colors.accentPrimary + '60'}
                onMouseLeave={e => e.currentTarget.style.borderColor = colors.borderColor}
              >
                <div style={styles.cardHeader}>
                  <span style={styles.cardName}>{template.name}</span>
                  <StatusBadge status={template.status} />
                </div>

                <div style={styles.cardBody}>{getBodyText(template)}</div>

                {/* Tags row */}
                <div style={styles.cardTags}>
                  <span style={styles.tag}>{template.language}</span>
                  <span style={styles.tag}>{template.category === 'UTILITY' ? 'Utilidade' : template.category === 'MARKETING' ? 'Marketing' : template.category}</span>
                  {template.quality_score && (
                    <QualityTag score={template.quality_score} />
                  )}
                </div>

                {/* Actions */}
                <div style={styles.cardActions}>
                  <button
                    onClick={() => { setSendModal(template); setSendPhone(''); }}
                    style={{ ...styles.actionBtn, color: colors.accentPrimary }}
                    disabled={template.status !== 'APPROVED'}
                    title={template.status !== 'APPROVED' ? 'Apenas templates aprovados podem ser enviados' : 'Enviar template'}
                  >
                    <Send size={14} /> Enviar
                  </button>
                  <button
                    onClick={() => openHistory(template.name)}
                    style={styles.actionBtn}
                    title="Ver historico de envios"
                  >
                    <History size={14} /> Historico
                  </button>
                  <button
                    onClick={() => handleDelete(template.name)}
                    disabled={deleting === template.name}
                    style={{ ...styles.actionBtn, marginLeft: 'auto', color: '#ef4444' }}
                    title="Deletar template"
                  >
                    {deleting === template.name
                      ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={14} />
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ======================== */}
      {/* Create Modal */}
      {/* ======================== */}
      {showCreateModal && (
        <div style={styles.overlay} onClick={() => !creating && setShowCreateModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                Novo Template
              </h3>
              <button
                onClick={() => !creating && setShowCreateModal(false)}
                style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome do Template</label>
                <input
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="ex: boas_vindas_desafio"
                  style={styles.input}
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Apenas letras minusculas, numeros e underscores
                </p>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Texto do Template</label>
                <textarea
                  value={formBody}
                  onChange={e => setFormBody(e.target.value)}
                  placeholder="Digite o texto do template. Use {{1}}, {{2}} para variáveis."
                  style={styles.textarea}
                  maxLength={1024}
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  {formBody.length}/1024 caracteres
                </p>
              </div>

              <div style={styles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ ...styles.label, marginBottom: 0 }}>Variáveis (opcional)</label>
                  <button
                    type="button"
                    onClick={addVariable}
                    style={{
                      background: 'none', border: `1px solid ${colors.borderColor}`,
                      color: colors.textSecondary, cursor: 'pointer',
                      padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <Plus size={12} /> Adicionar variável
                  </button>
                </div>
                {formVariables.length === 0 && (
                  <p style={{ fontSize: '11px', color: colors.textTertiary, margin: 0 }}>
                    Sem variáveis. Para tornar o template dinâmico, adicione variáveis e use os marcadores {'{{1}}'}, {'{{2}}'}… no texto.
                  </p>
                )}
                {formVariables.map((v, idx) => (
                  <div key={idx} style={{
                    padding: '10px', backgroundColor: colors.bgSecondary,
                    borderRadius: '8px', marginBottom: '6px',
                    display: 'flex', flexDirection: 'column', gap: '6px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                        Marcador {`{{${idx + 1}}}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeVariable(idx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        title="Remover"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <select
                      value={v.source}
                      onChange={e => updateVariable(idx, { source: e.target.value })}
                      style={styles.select}
                    >
                      <optgroup label="Sistema (auto)">
                        <option value="system:user_name">Nome do usuário (auto)</option>
                        <option value="system:saudacao">Saudação por horário (Bom dia/tarde/noite)</option>
                        <option value="system:data">Data atual (dd/MM/yyyy)</option>
                        <option value="system:horario">Horário programado do lembrete</option>
                      </optgroup>
                      <optgroup label="Relatório semanal (calculado)">
                        <option value="system:report:avg_calories_7d">Calorias médias 7d (kcal)</option>
                        <option value="system:report:avg_water_ml_7d">Água média 7d (ml)</option>
                        <option value="system:report:avg_water_l_7d">Água média 7d (L)</option>
                        <option value="system:report:exercise_days_7d">Dias com exercício 7d</option>
                        <option value="system:report:weight_change_7d">Variação de peso 7d</option>
                        <option value="system:report:consistency_pct_7d">Consistência 7d (%)</option>
                        <option value="system:report:insight_1">Insight 1 (heurística)</option>
                        <option value="system:report:insight_2">Insight 2 (heurística)</option>
                        <option value="system:report:suggestion">Sugestão (heurística)</option>
                      </optgroup>
                      <optgroup label="Customizadas">
                        <option value="custom:medicamento">Custom: medicamento</option>
                        <option value="custom:dosagem">Custom: dosagem</option>
                        <option value="custom:profissional">Custom: profissional</option>
                        <option value="custom:livre">Custom: livre (defina abaixo)</option>
                      </optgroup>
                    </select>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        value={v.key}
                        onChange={e => updateVariable(idx, { key: e.target.value })}
                        placeholder="chave (ex: medicamento)"
                        style={{ ...styles.input, flex: 1 }}
                      />
                      <input
                        value={v.label}
                        onChange={e => updateVariable(idx, { label: e.target.value })}
                        placeholder="rótulo visível"
                        style={{ ...styles.input, flex: 1 }}
                      />
                    </div>
                    {v.source === 'custom:livre' && (
                      <p style={{ fontSize: '11px', color: colors.textTertiary, margin: 0 }}>
                        Para custom livre, o source efetivo será <code>custom:{v.key || 'chave'}</code> — o agente pedirá esse valor ao criar lembretes.
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Categoria</label>
                  <select
                    value={formCategory}
                    onChange={e => setFormCategory(e.target.value)}
                    style={styles.select}
                  >
                    <option value="UTILITY">Utilidade</option>
                    <option value="MARKETING">Marketing</option>
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Idioma</label>
                  <select
                    value={formLanguage}
                    onChange={e => setFormLanguage(e.target.value)}
                    style={styles.select}
                  >
                    <option value="pt_BR">Portugues (BR)</option>
                    <option value="en_US">English (US)</option>
                    <option value="es">Espanol</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                style={{ ...styles.btn, ...styles.btnGhost }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !formName.trim() || !formBody.trim()}
                style={{
                  ...styles.btn, ...styles.btnPrimary,
                  opacity: creating || !formName.trim() || !formBody.trim() ? 0.6 : 1,
                }}
              >
                {creating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                {creating ? 'Criando...' : 'Criar Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* Send Modal */}
      {/* ======================== */}
      {sendModal && (
        <div style={styles.overlay} onClick={() => !sending && setSendModal(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                  Enviar Template
                </h3>
                <p style={{ fontSize: '12px', color: colors.textTertiary, margin: '2px 0 0' }}>
                  {sendModal.name}
                </p>
              </div>
              <button
                onClick={() => !sending && setSendModal(null)}
                style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              {/* Preview */}
              <div style={{
                padding: '12px 16px', borderRadius: '10px',
                backgroundColor: colors.bgSecondary, marginBottom: '16px',
                fontSize: '13px', color: colors.textSecondary, lineHeight: '1.5',
                whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto',
                border: `1px solid ${colors.borderColor}`,
              }}>
                {getBodyText(sendModal)}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Phone size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '4px' }} />
                  Telefone do destinatario
                </label>
                <input
                  value={sendPhone}
                  onChange={e => setSendPhone(e.target.value)}
                  placeholder="5551999999999"
                  style={styles.input}
                  autoFocus
                />
                <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Codigo do pais + DDD + numero (sem espacos ou tracos)
                </p>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setSendModal(null)}
                disabled={sending}
                style={{ ...styles.btn, ...styles.btnGhost }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !sendPhone.trim()}
                style={{
                  ...styles.btn, ...styles.btnPrimary,
                  opacity: sending || !sendPhone.trim() ? 0.6 : 1,
                }}
              >
                {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
                {sending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* History Modal */}
      {/* ======================== */}
      {historyModal && (
        <div style={styles.overlay} onClick={() => setHistoryModal(null)}>
          <div style={styles.modalLarge} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                  Historico de Envios
                </h3>
                <p style={{ fontSize: '12px', color: colors.textTertiary, margin: '2px 0 0', fontFamily: 'monospace' }}>
                  {historyModal}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => loadHistoryLogs(historyModal, historyPage, historyPhoneFilter)}
                  style={{ ...styles.actionBtn, padding: '6px' }}
                  title="Atualizar"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  onClick={() => setHistoryModal(null)}
                  style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Search */}
            <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.borderColor}` }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.textTertiary }} />
                <input
                  value={historyPhoneFilter}
                  onChange={e => handleHistoryPhoneChange(e.target.value)}
                  placeholder="Buscar por telefone..."
                  style={{
                    ...styles.input,
                    paddingLeft: '32px', fontSize: '13px',
                    backgroundColor: colors.bgSecondary,
                  }}
                />
              </div>
            </div>

            {/* Logs list */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              {historyLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Loader2 size={20} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : historyLogs.length === 0 ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', gap: '8px',
                }}>
                  <History size={32} color={colors.textTertiary} />
                  <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
                    {historyPhoneFilter ? 'Nenhum envio encontrado para este numero' : 'Nenhum envio registrado'}
                  </p>
                </div>
              ) : (
                <div>
                  {historyLogs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 20px',
                        borderBottom: idx < historyLogs.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                        gap: '12px',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px', fontWeight: 500, color: colors.textPrimary,
                          fontFamily: 'monospace',
                        }}>
                          {log.recipient_phone}
                        </div>
                        <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '2px' }}>
                          {formatDate(log.sent_at)}
                        </div>
                        {log.status === 'failed' && log.error_message && (
                          <div style={{
                            fontSize: '11px', color: '#ef4444', marginTop: '4px',
                            lineHeight: '1.3',
                          }}>
                            {log.error_message}
                          </div>
                        )}
                      </div>
                      <LogStatusBadge status={log.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {historyTotal > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
                fontSize: '12px', color: colors.textTertiary,
              }}>
                <span>{historyTotal} envio{historyTotal !== 1 ? 's' : ''}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleHistoryPageChange(historyPage - 1)}
                    disabled={historyPage <= 1 || historyLoading}
                    style={{
                      ...styles.actionBtn, padding: '4px 8px',
                      opacity: historyPage <= 1 ? 0.4 : 1,
                    }}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                    {historyPage} / {totalPages || 1}
                  </span>
                  <button
                    onClick={() => handleHistoryPageChange(historyPage + 1)}
                    disabled={historyPage >= totalPages || historyLoading}
                    style={{
                      ...styles.actionBtn, padding: '4px 8px',
                      opacity: historyPage >= totalPages ? 0.4 : 1,
                    }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TemplatesPage;
